import type { BenchmarkRunSummary } from '@owl/shared';
import { useEffect, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import { formatInt, formatUsd } from '../components/charts/AiUsageBlocks';
import { activateOnKey } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/time';

function passPct(r: BenchmarkRunSummary): number {
  const scored = r.passCount + r.partialCount + r.failCount;
  return scored > 0 ? Math.round((100 * r.passCount) / scored) : 0;
}

export function Benchmark({ ctx }: { ctx: ShellCtx }) {
  const [items, setItems] = useState<BenchmarkRunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch on refreshTick
  useEffect(() => {
    let active = true;
    api
      .listBenchmarkRuns()
      .then((res) => {
        if (active) setItems(res.items);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load');
      });
    return () => {
      active = false;
    };
  }, [ctx.refreshTick]);

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">Benchmarks</div>
          <div className="view-sub">
            {items?.length ?? 0} runs · agent cost &amp; correctness from simulated conversations
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          No benchmark runs yet. Run the benchmark with the <code>--owl</code> flag to publish one.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Model</th>
                <th>Scenarios</th>
                <th>Pass</th>
                <th>P / Pa / F</th>
                <th>Tokens/turn (in/out)</th>
                <th>Cost / conv</th>
                <th>Product cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  className="data-row"
                  tabIndex={0}
                  onClick={() => ctx.navigate({ name: 'benchmarkdetail', runId: r.id })}
                  onKeyDown={(e) =>
                    activateOnKey(e, () => ctx.navigate({ name: 'benchmarkdetail', runId: r.id }))
                  }
                >
                  <td>
                    <div>
                      <strong>{r.label || formatDateTime(r.createdAt)}</strong>
                      {r.mode === 'mock' && (
                        <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
                          (mock)
                        </span>
                      )}
                      {r.aborted && (
                        <span
                          style={{ marginLeft: 6, fontSize: 11, color: 'var(--danger, #d9534f)' }}
                        >
                          aborted
                        </span>
                      )}
                    </div>
                    <div className="dim" style={{ fontSize: 11 }}>
                      {formatDateTime(r.createdAt)} · {r.applicationName ?? 'unknown app'}
                    </div>
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {r.agentModel}
                  </td>
                  <td>{r.scenarioCount}</td>
                  <td>
                    <strong>{passPct(r)}%</strong>
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {r.passCount} / {r.partialCount} / {r.failCount}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {formatInt(r.avgInputTokensPerTurn)} / {formatInt(r.avgOutputTokensPerTurn)}
                  </td>
                  <td className="mono">{formatUsd(r.costPerConversationUsd)}</td>
                  <td className="mono">{formatUsd(r.productCostUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
